import { Box, Button, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import TaxSummaryHover from '../../../common/Tax/TaxSummaryHover';
import { chipStyle } from '../../../config/MaterialUIConstants';
import { CHIP_COLOR, INVOICE_PAYMENT_STATUS } from '../../../utility/Constant';
import { computeLabourPartsFromServices } from '../../../utility/helpers/computeLabourPartsFromServices';
import { formatDiscountAmountDisplay } from '../../../utility/helpers/discountCalculation';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import { DOCUMENT_TAX_TYPE, formatTaxPercent, isNewTaxVersion, summarizeTaxFromRecord } from '../../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../../utility/hooks/useTaxDecimalPrecision';
import { PAYMENT_METHODS } from '../GenrateInvoice/RecordPayementDialog';

const formatSummaryCurrency = (n) => (Number(n) > 0 ? NumericEngine.format(n, { formatCurrency: true, trailingZeros: true }) : '$0.00');

const InvoicePaymenetDetails = ({ invoiceData = {}, paymentDetails, isDraft = false }) => {
    const taxPrecision = useTaxDecimalPrecision();
    const { labourTotal, partsTotal, packagesTotal } = useMemo(() => {
        if (Array.isArray(invoiceData?.services) && invoiceData.services.length > 0) {
            return computeLabourPartsFromServices(invoiceData.services);
        }
        if (
            paymentDetails &&
            typeof paymentDetails.labourTotal === 'number' &&
            !Number.isNaN(paymentDetails.labourTotal) &&
            typeof paymentDetails.partsTotal === 'number' &&
            !Number.isNaN(paymentDetails.partsTotal)
        ) {
            const pkg = typeof paymentDetails.packagesTotal === 'number' && !Number.isNaN(paymentDetails.packagesTotal) ? paymentDetails.packagesTotal : 0;
            return { labourTotal: paymentDetails.labourTotal, partsTotal: paymentDetails.partsTotal, packagesTotal: pkg };
        }
        return { labourTotal: 0, partsTotal: 0, packagesTotal: 0 };
    }, [invoiceData?.services, paymentDetails?.labourTotal, paymentDetails?.partsTotal, paymentDetails?.packagesTotal]);

    // Show the STORED per-tax name/rate/amount from the GET response (calculation is authoritative
    // server-side). Backend top-level rows carry taxName + taxPercentage + taxAmount.
    const appliedTaxLines = useMemo(() => {
        if (!isNewTaxVersion(invoiceData)) return [];
        return summarizeTaxFromRecord(invoiceData, DOCUMENT_TAX_TYPE.INVOICE).filter((row) => row.applyTax && Number(row.taxAmount) > 0);
    }, [invoiceData]);

    // Sum of the per-tax amounts shown above — matches the invoice tax total.
    const totalTax = appliedTaxLines?.length > 0 ? appliedTaxLines.reduce((acc, r) => NumericEngine.add(acc, String(r.taxAmount || 0)), '0') : 0;

    const invoicePaymentStatus = invoiceData?.invoicePaymentStatus;

    const amountRefund = invoiceData?.totalAmountRefunded ? Number(invoiceData?.totalAmountRefunded) : 0;
    const amountPending = invoiceData?.totalAmountPending ? Number(invoiceData?.totalAmountPending) : 0;

    const isShowBalanceRemaining = invoicePaymentStatus === INVOICE_PAYMENT_STATUS.PARTIALLY_PAID || invoicePaymentStatus === INVOICE_PAYMENT_STATUS.UNPAID;
    const isShowBalanceOwing = invoicePaymentStatus === INVOICE_PAYMENT_STATUS.OVER_PAID;
    const isFullyPaid = invoicePaymentStatus === INVOICE_PAYMENT_STATUS.FULLY_PAID;

    const payments = [
        { date: 'March 27, 2025', type: 'Cash', amount: 250 },
        { date: 'March 27, 2025', type: 'Visa', amount: 250 },
    ];

    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const amountDue = 750;

    return (
        <Grid container justifyContent={'space-between'} padding={'0 16px 24px 24px'}>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Labour</Typography>
                <Typography>{formatSummaryCurrency(labourTotal)}</Typography>
            </Stack>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Parts</Typography>
                <Typography>{formatSummaryCurrency(partsTotal)}</Typography>
            </Stack>
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Service Packages</Typography>
                <Typography>{formatSummaryCurrency(packagesTotal)}</Typography>
            </Stack>
            <Divider sx={{ backgroundColor: '#0000001F', margin: '8px 0', width: '100%' }} />
            <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                <Typography>Subtotal</Typography>
                <Typography>
                    {invoiceData?.subTotal ? NumericEngine.format(invoiceData.subTotal, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                </Typography>
            </Stack>
            {invoiceData?.discount > 0 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                    <Box>
                        {invoiceData?.membershipProgram?.programName
                            ? `Discount - ${invoiceData?.membershipProgram?.programName} (${invoiceData?.membershipProgram?.discount}%)`
                            : 'Discount'}
                    </Box>
                    <Typography>
                        {formatDiscountAmountDisplay({
                            discount: invoiceData?.discount,
                            discountPercentage: invoiceData?.discountPercentage,
                            discountSource: invoiceData?.discountSource,
                        })}
                    </Typography>
                </Stack>
            )}
            {invoiceData?.totalFeesAmount > 0 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={1}>
                    <Typography>Fees</Typography>

                    <Typography>
                        {invoiceData?.totalFeesAmount
                            ? NumericEngine.format(invoiceData.totalFeesAmount, { formatCurrency: true, trailingZeros: true })
                            : '$0.00'}
                    </Typography>
                </Stack>
            )}
            {isNewTaxVersion(invoiceData) ? (
                appliedTaxLines.map((row) => (
                    <Stack
                        key={row.taxId || row.name}
                        direction={'row'}
                        width={'100%'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        gap={2}
                        mb={'8px'}
                    >
                        <TaxSummaryHover summary={row}>
                            <Typography>
                                {row.name} ({formatTaxPercent(row.rate, taxPrecision)}%)
                            </Typography>
                        </TaxSummaryHover>
                        {/* Amount is a second, independent trigger for the same card — same as the
                            editable summary, where the name and the amount each open it. */}
                        <TaxSummaryHover summary={row}>
                            <Typography>
                                {Number(row.taxAmount) > 0 ? NumericEngine.format(row.taxAmount, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                            </Typography>
                        </TaxSummaryHover>
                    </Stack>
                ))
            ) : (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={0}>
                    <Typography>Tax</Typography>
                    <Typography>{invoiceData?.tax ? NumericEngine.format(invoiceData.tax, { formatCurrency: true, trailingZeros: true }) : '$0.00'}</Typography>
                </Stack>
            )}
            {/* Total tax line  */}
            {isNewTaxVersion(invoiceData) && appliedTaxLines?.length > 1 && (
                <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={2} mb={'8px'}>
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>Total Tax</Typography>
                    <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>{formatSummaryCurrency(totalTax)}</Typography>
                </Stack>
            )}

            <Divider sx={{ backgroundColor: '#0000001F', margin: '4px 0 12px 0', width: '100%' }} />

            <Grid item xs={6}>
                <Typography fontWeight={600} sx={{ fontSize: '18px' }}>
                    Total
                </Typography>
            </Grid>
            <Grid item xs={6} textAlign={'end'}>
                <Typography fontWeight={600} sx={{ fontSize: '18px' }}>
                    {invoiceData?.Total ? NumericEngine.format(invoiceData.Total, { formatCurrency: true, trailingZeros: true }) : '$0.00'} <br />
                    {/* {paymentDetails?.total ? formatCurrency(paymentDetails.total) : '$0.00'} */}
                </Typography>
            </Grid>

            {invoiceData?.payments?.length > 0 && (
                <Box sx={{ width: '100%', marginTop: '16px' }}>
                    <TableContainer elevation={0}>
                        <Table>
                            <TableHead style={{ backgroundColor: '#F8F8F8' }}>
                                <TableRow>
                                    <TableCell width={150} sx={{ padding: '4px 8px', border: 'none', fontSize: '16px', fontWeight: '600' }}>
                                        Date
                                    </TableCell>

                                    <TableCell width={111} sx={{ padding: '4px 8px', border: 'none', fontSize: '16px', fontWeight: '600' }}>
                                        Type
                                    </TableCell>
                                    <TableCell sx={{ padding: '4px 8px', border: 'none', fontSize: '16px', fontWeight: '600' }}>Reference</TableCell>
                                    <TableCell width={150} align="right" sx={{ padding: '4px 8px', border: 'none', fontSize: '16px', fontWeight: '600' }}>
                                        Amount
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoiceData?.payments.map((payment, index) => {
                                    const method = PAYMENT_METHODS.find((m) => m.key === payment.paymentMethod);
                                    return (
                                        <TableRow key={index} style={{ verticalAlign: 'baseline' }}>
                                            <TableCell sx={{ padding: '4px 8px', fontSize: '16px', border: 'none' }}>
                                                {payment.paymentDate ? dayjs(payment.paymentDate).format('MMMM DD, YYYY') : '-'}
                                            </TableCell>
                                            <TableCell sx={{ padding: '4px 8px', fontSize: '16px', border: 'none' }}>
                                                {method?.logo ? (
                                                    <>
                                                        <img
                                                            src={method.logo}
                                                            alt={method.label}
                                                            style={{ width: 'auto', height: '20px', verticalAlign: 'middle', marginRight: 4 }}
                                                        />
                                                        {method.label}
                                                    </>
                                                ) : (
                                                    method?.label || payment.paymentMethod
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ padding: '4px 8px', fontSize: '16px', border: 'none' }}>
                                                {payment?.paymentReference || ''}
                                            </TableCell>
                                            <TableCell
                                                sx={{ padding: '4px 8px', fontSize: '16px', border: 'none', alignItems: 'center', display: 'flex' }}
                                                align="right"
                                            >
                                                <span style={{ display: 'block' }}>
                                                    {NumericEngine.format(payment?.amount, { formatCurrency: true, trailingZeros: true })}
                                                </span>
                                                {/* <Edit
                                                    size={16}
                                                    style={{
                                                        color: '#646466',
                                                        width: '20px',
                                                        height: '20px',
                                                        cursor: 'pointer',
                                                        marginRight: '4px',
                                                    }}
                                                /> */}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {!isFullyPaid && invoiceData?.payments?.length > 1 && <Divider sx={{ backgroundColor: '#0000001F', margin: '12px 0' }} />}

                    {!isFullyPaid && invoiceData?.payments?.length > 1 && (
                        <Box display="flex" justifyContent="space-between" mt={0}>
                            <Typography sx={{ padding: '0 8px', fontSize: '16px', fontWeight: '400' }}>Total Amount Paid</Typography>
                            <Typography variant="subtitle1" sx={{ padding: '0 8px', fontSize: '16px', fontWeight: '400' }}>
                                {invoiceData?.totalAmountPaid
                                    ? NumericEngine.format(invoiceData?.totalAmountPaid, { formatCurrency: true, trailingZeros: true })
                                    : '$0.00'}
                            </Typography>
                        </Box>
                    )}

                    {isShowBalanceRemaining && <Divider sx={{ backgroundColor: '#0000001F', margin: '12px 0' }} />}
                    {isShowBalanceRemaining && (
                        <Box display="flex" justifyContent="space-between" mt={0}>
                            <Typography sx={{ padding: '0 8px', fontSize: '18px', fontWeight: '500', lineHeight: '150%' }} variant="subtitle1">
                                Amount Due
                            </Typography>
                            <Typography variant="subtitle1" sx={{ padding: '0 8px', fontSize: '18px', fontWeight: '500', lineHeight: '150%' }}>
                                {Number(invoiceData?.totalAmountPending) > 0
                                    ? NumericEngine.format(invoiceData.totalAmountPending, { formatCurrency: true, trailingZeros: true })
                                    : '$0.00'}
                            </Typography>
                        </Box>
                    )}

                    {isFullyPaid && (
                        <div
                            style={{
                                ...chipStyle,
                                backgroundColor: CHIP_COLOR.FULLY_PAID,
                                color: '#fff',
                                height: '35px',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                width: '100%',
                                marginTop: '16px',
                            }}
                        >
                            {/* <CheckCircle /> */}
                            <span>Fully Paid</span>
                        </div>
                    )}

                    {isShowBalanceOwing && (
                        <div
                            style={{
                                ...chipStyle,
                                backgroundColor: CHIP_COLOR.BALANCE_OWING,
                                color: CHIP_COLOR.BALANCE_OWING_COLOR,
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 8px',
                                height: '35px',
                                borderRadius: '6px',
                                width: '100%',
                                marginTop: '16px',
                            }}
                        >
                            <span>Balance Owing</span>{' '}
                            <span>{NumericEngine.format(Math.abs(+amountPending), { formatCurrency: true, trailingZeros: true })} </span>
                        </div>
                    )}
                </Box>
            )}

            {!isDraft && invoiceData?.paymentMethod && (
                <>
                    <Grid item xs={6}>
                        <Typography fontWeight={500} lineHeight={'150%'} letterSpacing={'0.15px'} sx={{ padding: '8px' }}>
                            Payment Method:
                        </Typography>
                    </Grid>
                    <Grid item xs={6} textAlign={'end'}>
                        <Typography style={{ textTransform: 'capitalize', padding: '8px' }}>&nbsp; {invoiceData?.paymentMethod}</Typography>
                    </Grid>
                </>
            )}

            {false && (
                <Grid item xs={6} style={{ marginTop: '24px', paddingLeft: '8px' }}>
                    <Button variant="contained" color="error" size="large">
                        Refund
                    </Button>
                </Grid>
            )}
            {false && (
                <Grid item xs={6} textAlign={'end'} style={{ marginTop: '24px', paddingRight: '8px' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        // onClick={handelCloseModel}
                        size="large"
                        type="submit"
                    >
                        RECORD PAYMENT
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};

export default InvoicePaymenetDetails;
